import { type ReactNode } from "react";
import clsx from "clsx";

/**
 * Emphasis — the Aesop "selective-emphasis" device (DESIGN.md §2.1).
 *
 * Highlights a key word/phrase within otherwise plain near-black text, creating a structured,
 * "partially highlighted" hierarchy. Our signature typographic pattern — used sparingly (2–3×
 * per view) so it stays special.
 *
 * Modes:
 *   - "gold"   (default) — the word rendered in the deep gold accent
 *   - "italic" — italic serif (works when the surrounding text is serif)
 *   - "line"   — quiet gold underline beneath the word
 *
 * Usage:
 *   <p>Crystallized <Emphasis>acetaminophen</Emphasis>, photographed under polarized light.</p>
 *   <h1 className="font-serif">Crystals, <Emphasis mode="italic">rendered as light.</Emphasis></h1>
 */
export function Emphasis({
	children,
	mode = "gold",
	className,
}: {
	children: ReactNode;
	mode?: "gold" | "italic" | "line";
	className?: string;
}) {
	return (
		<em
			className={clsx(
				"not-italic",
				mode === "gold" && "text-mg-gold-deep",
				mode === "italic" && "font-serif italic",
				mode === "line" && "underline decoration-mg-gold-deep/60 decoration-1 underline-offset-4",
				className,
			)}
		>
			{children}
		</em>
	);
}
